import { MenuItem, Typography } from '@mui/material';
import { useState } from 'react';

import { uploadPreFlightDocument } from '../../../../api/preFlight';
import {
  convertDocumentType,
  PreFlightDocumentType,
} from '../../../../api/preFlight/enumerations';
import { Constants } from '../../constants';
import { AttachedFileRow } from './AttachedFileRow';
import { FileDropBox } from './FileDropBox';
import {
  DialogContainer,
  FilledButton,
  OutlinedButton,
  ReportFieldBox,
  ReportFieldLabel,
  ReportFooter,
  ReportFooterHint,
  ReportTypeSelect,
  StyledDialog,
  TextTypography,
} from './styles';

/** Um anexo escolhido, ainda não enviado. */
interface Anexo {
  id: number;
  file: File;
  type: PreFlightDocumentType;
}

interface ReportAttachmentsModalProps {
  osId: number;
  open: boolean;
  onClose: () => void;
  /**
   * Entrega o laudo e conclui a OS.
   *
   * Delegado ao pai em vez de chamado aqui: é ele que já trata o carregamento, o snackbar e
   * a recarga da lista depois da conclusão. Duplicar aqui criaria um segundo caminho de erro
   * para o mesmo pedido, e os dois envelheceriam separados.
   */
  onCompleteReport: (file: File) => Promise<void>;
}

const TIPOS = Object.values(PreFlightDocumentType).filter(
  (t) => t !== PreFlightDocumentType.SELECT
);

/**
 * Anexar o laudo e os outros documentos, e então concluir a OS (PROT-2980).
 *
 * POR QUE O LAUDO É UMA LINHA À PARTE, E NÃO MAIS UM ANEXO
 *
 * Ele não é um documento entre outros: é o que FECHA a ordem de serviço. `POST /report`
 * muda o estado da solicitação e dispara o webhook que devolve tudo ao RPAM. Tratá-lo como
 * um anexo qualquer faria a ordem de envio virar detalhe de implementação — e a ordem aqui
 * é regra.
 *
 * POR QUE O LAUDO VAI POR ÚLTIMO
 *
 * Depois dele a OS está concluída e não aceita mais nada. Mandá-lo primeiro faria os demais
 * anexos chegarem numa OS já fechada e voltarem erro, um a um, sem que ninguém entendesse
 * por quê.
 *
 * POR QUE UM ANEXO QUE FALHA NÃO CANCELA OS OUTROS
 *
 * Cada anexo é uma chamada própria — o endpoint recebe um arquivo por vez. Abortar tudo no
 * primeiro erro desperdiçaria os que já subiram e obrigaria a pessoa a recomeçar sem saber
 * quais tinham dado certo. O modal continua e diz, no fim, o que ficou de fora.
 */
export function ReportAttachmentsModal({
  osId,
  open,
  onClose,
  onCompleteReport,
}: ReportAttachmentsModalProps): JSX.Element {
  const [laudo, setLaudo] = useState<File | undefined>(undefined);
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [falhas, setFalhas] = useState<string[]>([]);

  // Recebem `File[]` porque a escolha agora chega tanto do clique quanto do
  // arraste. A validação continua aqui, e não no FileDropBox: a regra do laudo
  // é deste modal, e a caixa tracejada é só apresentação.
  const escolherLaudo = (arquivos: File[]): void => {
    const file = arquivos[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setErro(Constants.completeOsOnlyPdf);
      return;
    }
    setErro(null);
    setLaudo(file);
  };

  const escolherAnexos = (arquivos: File[]): void => {
    setAnexos((atuais) => [
      ...atuais,
      ...arquivos.map((file, i) => ({
        id: Date.now() + i,
        file,
        type: PreFlightDocumentType.OTHER,
      })),
    ]);
  };

  const trocarTipo = (id: number, type: PreFlightDocumentType): void =>
    setAnexos((atuais) =>
      atuais.map((a) => (a.id === id ? { ...a, type } : a))
    );

  const remover = (id: number): void =>
    setAnexos((atuais) => atuais.filter((a) => a.id !== id));

  const enviar = async (): Promise<void> => {
    if (!laudo) {
      setErro(Constants.attachmentsReportRequired);
      return;
    }
    setEnviando(true);
    setErro(null);

    // EM SÉRIE, e não em paralelo. `reduce` encadeando promessas em vez de laço porque o
    // lint do projeto proíbe `for...of` — o encadeamento preserva a ordem do mesmo jeito.
    //
    // Paralelo poria várias gravações concorrendo pela mesma solicitação, e a diferença de
    // tempo é imperceptível para quem acabou de escolher os arquivos.
    const naoEnviados: string[] = [];
    await anexos.reduce(async (anterior, anexo) => {
      await anterior;
      const formData = new FormData();
      formData.append('file', anexo.file);
      formData.append('type', anexo.type);
      try {
        await uploadPreFlightDocument(osId, formData);
      } catch {
        naoEnviados.push(anexo.file.name);
      }
    }, Promise.resolve());
    setFalhas(naoEnviados);

    try {
      await onCompleteReport(laudo);
      onClose();
    } catch {
      setErro(Constants.attachmentsCompleteFailed);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={enviando ? undefined : onClose}
      maxWidth="sm"
    >
      <DialogContainer>
        <TextTypography>{Constants.attachmentsTitle}</TextTypography>

        <ReportFieldBox>
          <ReportFieldLabel>
            {Constants.attachmentsReportLabel}
          </ReportFieldLabel>
          {laudo ? (
            <AttachedFileRow
              file={laudo}
              removeTitle={Constants.attachmentsChangeReport}
              onRemove={() => setLaudo(undefined)}
            />
          ) : (
            <FileDropBox
              inputId="preFlightReportUpload"
              accept="application/pdf"
              title={Constants.attachmentsDropReport}
              subtitle={Constants.attachmentsDropReportFormat}
              onSelect={escolherLaudo}
            />
          )}
        </ReportFieldBox>

        <ReportFieldBox>
          <ReportFieldLabel>
            {Constants.attachmentsOthersLabel}
          </ReportFieldLabel>
          {anexos.map((anexo) => (
            <AttachedFileRow
              key={anexo.id}
              file={anexo.file}
              removeTitle={Constants.attachmentsRemove}
              onRemove={() => remover(anexo.id)}
            >
              <ReportTypeSelect
                select
                size="small"
                value={anexo.type}
                onChange={(e) =>
                  trocarTipo(anexo.id, e.target.value as PreFlightDocumentType)
                }
              >
                {TIPOS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {convertDocumentType(t)}
                  </MenuItem>
                ))}
              </ReportTypeSelect>
            </AttachedFileRow>
          ))}
          <FileDropBox
            inputId="preFlightAttachmentsUpload"
            multiple
            title={Constants.attachmentsDropOthers}
            subtitle={Constants.attachmentsDropOthersFormat}
            onSelect={escolherAnexos}
          />
        </ReportFieldBox>

        {erro && <Typography color="error">{erro}</Typography>}
        {falhas.length > 0 && (
          <Typography color="error">
            {Constants.attachmentsPartial} {falhas.join(', ')}
          </Typography>
        )}

        <ReportFooter>
          {/* O aviso explica o botao desabilitado ANTES do clique: antes ele so
              aparecia depois de um clique que nao fazia nada. */}
          {!laudo && (
            <ReportFooterHint>
              {Constants.attachmentsFooterHint}
            </ReportFooterHint>
          )}
          <OutlinedButton onClick={onClose} disabled={enviando}>
            {Constants.attachmentsCancel}
          </OutlinedButton>
          <FilledButton onClick={enviar} disabled={enviando || !laudo}>
            {enviando ? Constants.attachmentsSending : Constants.completeOs}
          </FilledButton>
        </ReportFooter>
      </DialogContainer>
    </StyledDialog>
  );
}
